'use client';

import type { ReactNode } from 'react';
import { AuroraField } from './AuroraField';
import { SlikerrLockup } from './SlikerrLockup';
import styles from './AuroraForm.module.css';

/**
 * The form side of the brand door: the same aurora field, the same lockup, and
 * a single column of fields sitting straight on it — no card.
 *
 * ## Why it is a shell and not a form
 *
 * `/login` has four steps behind the door (email, code, pick a business, create
 * one) and the wizard has two more. They differ entirely in what they ask and
 * not at all in how they are framed, so this owns the frame and each caller
 * passes its own fields as `children`. Nothing about a step's logic moves here.
 *
 * ## Why no card
 *
 * The owner chose this over three card treatments (2026-08-20). The card threw
 * away the field the door had just shown — two screens of one flow that looked
 * like two products. See `AuroraForm.module.css` for what holds the form
 * together instead of a border, and for the contrast bargain it shares with
 * `AuroraField`.
 *
 * ## Its sibling
 *
 * `AuroraIntro` is the same surface with a headline and one ink pill instead of
 * fields. They are deliberately two components rather than one with a `mode`:
 * a door and a form want different measures (34rem vs 22rem) and different
 * alignment, and every prop that differed would have to be threaded through.
 * What they share — the field, the lockup, the quiet back link's exact styling
 * — they share by importing the same things.
 */

/** The class a caller's `LanguageToggle` must carry to look like part of this
 *  surface. Exported rather than applied by the shell, because the toggle is
 *  passed in as an already-built node. */
export const auroraFormLangClass = styles.langButton;

export function AuroraForm({
  title,
  subtitle,
  children,
  languageToggle,
  backLabel,
  onBack,
  testId,
}: {
  /** The one line saying what this step wants. Sits under the lockup. */
  title: string;
  /** An optional second line, for a step whose title alone does not say what
   *  happens next. `/login` has none; the wizard's sign-in explains that a code
   *  is coming and that the setup continues after it. */
  subtitle?: string;
  /** The step's own fields and actions. */
  children: ReactNode;
  /** Rendered floated in the corner, over the field. Pass
   *  `<LanguageToggle className={auroraFormLangClass} />` so it picks up the
   *  pill styling — the component is shared and renders a bare button. */
  languageToggle?: ReactNode;
  /** The way back to the door. Omitted on steps that have nothing to go back
   *  to — after a code is verified, the door is no longer a place to return. */
  backLabel?: string;
  onBack?: () => void;
  testId?: string;
}) {
  return (
    <main className={styles.page} data-testid={testId}>
      <AuroraField />

      {languageToggle && <div className={styles.lang}>{languageToggle}</div>}

      <div className={styles.column}>
        <div className={styles.crest}>
          <SlikerrLockup />
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        <div className={styles.body}>{children}</div>

        {backLabel && onBack && (
          <button
            type="button"
            className={styles.back}
            onClick={onBack}
            data-testid={testId ? `${testId}-back` : undefined}
          >
            {backLabel}
          </button>
        )}
      </div>
    </main>
  );
}
